import { useMutation, useQuery } from '@tanstack/react-query';

import { supabase } from '@/lib/supabase';
import { adminFetch } from './useAdminApi';

/**
 * useBillingRun — hooks for the admin Billing Run console (/admin/billing).
 *
 *   useEnabledStatementCustomers() — the hand-pick universe (active customers).
 *   useBillingRunDryRun()          — POST dry_run:true → counts + est cost.
 *   useBillingRunStart()           — POST dry_run:false → { run_id }; fire run.
 *   useBillingRunProgress(runId)   — poll orders.billing_run + _item via RLS.
 *
 * Recipient resolution + the paper-hand-pick-only guard live server-side in
 * admin-billing-run; these hooks only shuttle the request and read progress.
 */

export type BillingChannel = 'email' | 'paper' | 'both';
export type BillingAudience = 'all' | 'selected' | 'paper_opted_in';

export type BillingDocType = 'statement' | 'invoice' | 'both';

export interface ChannelPlan {
  docs: Array<'statement' | 'invoice'>;
  invoiceIds: string[];
}

export interface BillingRunRecipient {
  id: string;
  name: string;
  channels: Array<'email' | 'paper'>;
  docType: BillingDocType;
  plan: { email?: ChannelPlan; paper?: ChannelPlan };
}

export interface BillingRunDryRunResult {
  ok: true;
  dry_run: true;
  email_count: number;
  paper_count: number;
  /** Total paper LETTERS (statements + per-invoice) — drives postage. */
  paper_letter_count: number;
  doc_type_counts: Partial<Record<BillingDocType, number>>;
  est_cost: number;
  recipients: BillingRunRecipient[];
}

export interface BillingRunStartResult {
  ok: true;
  run_id: string;
  total: number;
  email_count: number;
  paper_count: number;
  paper_letter_count: number;
  doc_type_counts: Partial<Record<BillingDocType, number>>;
  est_cost: number;
}

export interface BillingRunRequest {
  channel: BillingChannel;
  audience: BillingAudience;
  customer_ids?: string[];
}

/* -------------------- Hand-pick universe -------------------- */

export interface EnabledCustomer {
  id: string;
  name: string;
  statements_enabled: boolean;
  paper_statements_enabled: boolean;
}

/**
 * Active customers, for the hand-pick multi-select. Read directly via the
 * superadmin SELECT RLS on orders.customers (same posture as the detail page).
 * Carries paper_statements_enabled so the console can show the "opted into
 * paper" count without a separate query.
 */
export function useEnabledStatementCustomers() {
  return useQuery<EnabledCustomer[]>({
    queryKey: ['admin', 'billing-run', 'enabled-customers'],
    staleTime: 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('orders')
        .from('customers')
        .select('id,name,statements_enabled,paper_statements_enabled')
        .eq('active', true)
        .order('name');
      if (error) throw error;
      return (data ?? []) as unknown as EnabledCustomer[];
    },
  });
}

/* -------------------- Dry run + start -------------------- */

export function useBillingRunDryRun() {
  return useMutation<BillingRunDryRunResult, Error, BillingRunRequest>({
    mutationFn: (input) =>
      adminFetch<BillingRunDryRunResult>('admin-billing-run', {
        method: 'POST',
        body: JSON.stringify({ ...input, dry_run: true }),
      }),
  });
}

export function useBillingRunStart() {
  return useMutation<BillingRunStartResult, Error, BillingRunRequest>({
    mutationFn: (input) =>
      adminFetch<BillingRunStartResult>('admin-billing-run', {
        method: 'POST',
        body: JSON.stringify({ ...input, dry_run: false }),
      }),
  });
}

/* -------------------- Progress polling -------------------- */

export interface BillingRunRow {
  id: string;
  channel: BillingChannel;
  audience: BillingAudience;
  status: 'pending' | 'running' | 'done' | 'error';
  total: number;
  sent_email: number;
  sent_paper: number;
  failed: number;
  est_cost: number | null;
  created_at: string;
  finished_at: string | null;
  error: string | null;
}

export interface BillingRunItemRow {
  id: string;
  run_id: string;
  customer_id: string | null;
  channel: 'email' | 'paper';
  status: 'pending' | 'sent' | 'skipped' | 'error';
  error: string | null;
  created_at: string;
}

export interface BillingRunProgress {
  run: BillingRunRow | null;
  items: BillingRunItemRow[];
}

/**
 * Poll a single run's header + items every 1.5s until it reaches a terminal
 * state (done | error). The UI reads `.data?.run.status` to stop spinning.
 */
export function useBillingRunProgress(runId: string | null) {
  return useQuery<BillingRunProgress>({
    queryKey: ['admin', 'billing-run', 'progress', runId],
    enabled: !!runId,
    refetchInterval: (query) => {
      const status = query.state.data?.run?.status;
      return status === 'done' || status === 'error' ? false : 1500;
    },
    queryFn: async () => {
      const { data: runs, error: rErr } = await supabase
        .schema('orders')
        .from('billing_run')
        .select(
          'id,channel,audience,status,total,sent_email,sent_paper,failed,est_cost,created_at,finished_at,error',
        )
        .eq('id', runId!)
        .maybeSingle();
      if (rErr) throw rErr;

      const { data: items, error: iErr } = await supabase
        .schema('orders')
        .from('billing_run_item')
        .select('id,run_id,customer_id,channel,status,error,created_at')
        .eq('run_id', runId!)
        .order('created_at', { ascending: true });
      if (iErr) throw iErr;

      return {
        run: (runs as unknown as BillingRunRow) ?? null,
        items: (items ?? []) as unknown as BillingRunItemRow[],
      };
    },
  });
}
